import { createContext, useContext, useMemo } from 'react';
import { SessionContext } from 'contexts/session';
import { normalizeCapabilities } from './adapter';
import { deriveCapabilitySelectors } from './selectors';
import { CapabilitiesContextValue, CapabilityFlags } from './types';

export const CapabilitiesContext = createContext<CapabilitiesContextValue | null>(null);

interface CapabilitiesProviderProps {
  children: JSX.Element | JSX.Element[];
  overrideCapabilities?: Partial<CapabilityFlags>;
}

export const CapabilitiesProvider = ({
  children,
  overrideCapabilities,
}: CapabilitiesProviderProps): JSX.Element => {
  const session = useContext(SessionContext);
  const rawFeatures = session?.features;
  const failOpen = !session?.isExpired;

  const value = useMemo<CapabilitiesContextValue>(() => {
    const capabilities: CapabilityFlags = {
      ...normalizeCapabilities(rawFeatures, { failOpen }),
      ...overrideCapabilities,
    };

    return { capabilities, ...deriveCapabilitySelectors(capabilities) };
  }, [rawFeatures, session?.isExpired, overrideCapabilities]);

  return <CapabilitiesContext.Provider value={value}>{children}</CapabilitiesContext.Provider>;
};

export const useCapabilities = (): CapabilitiesContextValue => {
  const context = useContext(CapabilitiesContext);
  if (!context) {
    throw new Error('useCapabilities must be used within a CapabilitiesProvider');
  }
  return context;
};
